import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { getApiBaseUrl } from '../utils/apiClient';

// W3C-konformer Kontrastcheck: gibt '#ffffff' für dunkle, '#1F1F1F' für helle Hintergründe zurück.
function getContrastColor(hex: string): string {
  const [r, g, b] = hexToRgb(hex);
  const toLinear = (c: number) => {
    const s = c / 255;
    return s <= 0.03928 ? s / 12.92 : Math.pow((s + 0.055) / 1.055, 2.4);
  };
  const L = 0.2126 * toLinear(r) + 0.7152 * toLinear(g) + 0.0722 * toLinear(b);
  return L > 0.179 ? '#1F1F1F' : '#ffffff';
}

// Injects (or updates) a <style id="brand-nav-styles"> tag with dark-mode-aware nav rules.
// Text color is computed automatically from the background via WCAG contrast ratio.
function injectBrandNavStyles(navBgColor: string) {
  const STYLE_ID = 'brand-nav-styles';
  let el = document.getElementById(STYLE_ID) as HTMLStyleElement | null;
  if (!el) {
    el = document.createElement('style');
    el.id = STYLE_ID;
    document.head.appendChild(el);
  }

  const rules: string[] = [];

  if (navBgColor) {
    const [r, g, b] = hexToRgb(navBgColor);
    const darkR = Math.round(r * 0.8);
    const darkG = Math.round(g * 0.8);
    const darkB = Math.round(b * 0.8);
    const darkBgHex = `#${darkR.toString(16).padStart(2, '0')}${darkG.toString(16).padStart(2, '0')}${darkB.toString(16).padStart(2, '0')}`;

    const lightTextColor = getContrastColor(navBgColor);
    const darkTextColor = getContrastColor(darkBgHex);

    rules.push(`.brand-nav-bg { background-color: ${navBgColor} !important; }`);
    rules.push(
      `.dark .brand-nav-bg { background-color: rgb(${darkR}, ${darkG}, ${darkB}) !important; }`
    );
    rules.push(`.brand-nav-text { color: ${lightTextColor} !important; }`);
    rules.push(`.dark .brand-nav-text { color: ${darkTextColor} !important; }`);

    // Light mode: when nav bg is dark, child elements with hardcoded gray text classes
    // (e.g. text-gray-600 on icons) would be invisible – override them to inherit the
    // white text color set on the nav container.
    if (lightTextColor === '#ffffff') {
      rules.push(
        [
          '.brand-nav-bg .text-gray-400',
          '.brand-nav-bg .text-gray-500',
          '.brand-nav-bg .text-gray-600',
          '.brand-nav-bg .text-gray-700',
          '.brand-nav-bg .text-gray-800',
          '.brand-nav-bg .text-gray-900',
          '.brand-nav-bg .text-brand-accent-700',
        ].join(', ') + ' { color: rgba(255,255,255,0.85) !important; }'
      );

      // Restore standard dark text for form elements (e.g. Select, Input) that have
      // their own white/light background inside the nav – prevents white-on-white text.
      // IMPORTANT: Must use `html:not(.dark)` (not just `:not(.dark)`) to specifically
      // target the <html> root element. Using `:not(.dark)` alone would also match
      // intermediate elements like <body> or <div> wrappers that don't have the dark
      // class, causing the rule to incorrectly apply even in dark mode.
      rules.push(
        'html:not(.dark) .brand-nav-bg .bg-white { color: rgb(17, 24, 39) !important; }' +
          '\nhtml:not(.dark) .brand-nav-bg .bg-white .text-gray-400 { color: rgb(156, 163, 175) !important; }' +
          '\nhtml:not(.dark) .brand-nav-bg .bg-white .text-gray-500 { color: rgb(107, 114, 128) !important; }' +
          '\nhtml:not(.dark) .brand-nav-bg .bg-white .text-gray-600 { color: rgb(75, 85, 99) !important; }' +
          '\nhtml:not(.dark) .brand-nav-bg .bg-white .text-gray-700 { color: rgb(55, 65, 81) !important; }' +
          '\nhtml:not(.dark) .brand-nav-bg .bg-white .text-gray-800 { color: rgb(31, 41, 55) !important; }' +
          '\nhtml:not(.dark) .brand-nav-bg .bg-white .text-gray-900 { color: rgb(17, 24, 39) !important; }'
      );

      // Hover/active backgrounds: replace light grays with low-opacity white overlays
      rules.push(
        [
          '.brand-nav-bg .hover\\:bg-gray-50:hover',
          '.brand-nav-bg .hover\\:bg-gray-100:hover',
          '.brand-nav-bg .hover\\:bg-primary-50:hover',
          '.brand-nav-bg .hover\\:bg-brand-accent-50:hover',
        ].join(', ') + ' { background-color: rgba(255,255,255,0.10) !important; }'
      );

      // Active nav item: replace light accent background with a white overlay
      rules.push(
        '.brand-nav-bg .bg-brand-accent-100 { background-color: rgba(255,255,255,0.18) !important; }'
      );
    }

    // Dark-mode content backgrounds – subtly tinted with the nav hue so the contrast
    // to the pure black default (bg-gray-900) is softened.  The tint factor is low
    // enough to stay very dark; only the hue „bleeds in" gently.
    const contentR = Math.round(r * 0.15 + 18 * 0.85);
    const contentG = Math.round(g * 0.15 + 18 * 0.85);
    const contentB = Math.round(b * 0.15 + 18 * 0.85);
    // Footer is one step lighter than the main area
    const footerR = Math.round(r * 0.18 + 26 * 0.82);
    const footerG = Math.round(g * 0.18 + 26 * 0.82);
    const footerB = Math.round(b * 0.18 + 26 * 0.82);

    rules.push(
      `.dark .brand-main-bg { background-color: rgb(${contentR}, ${contentG}, ${contentB}) !important; }`
    );
    rules.push(
      `.dark .brand-footer-bg { background-color: rgb(${footerR}, ${footerG}, ${footerB}) !important; }`
    );
  }

  el.textContent = rules.join('\n');
}

function removeBrandNavStyles() {
  document.getElementById('brand-nav-styles')?.remove();
}

// Converts a hex color to [R, G, B] channels
function hexToRgb(hex: string): [number, number, number] {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  return result
    ? [parseInt(result[1], 16), parseInt(result[2], 16), parseInt(result[3], 16)]
    : [0, 0, 0];
}

// Mix two RGB colors: ratio 0 = fully base, 1 = fully mixer
function mix(
  base: [number, number, number],
  mixer: [number, number, number],
  ratio: number
): string {
  return [0, 1, 2].map(i => Math.round(base[i] + (mixer[i] - base[i]) * ratio)).join(' ');
}

// Generate an RGB-channel palette (for Tailwind CSS variables) from a single hex
function buildPalette(hex: string): Record<string, string> {
  const base = hexToRgb(hex);
  const white: [number, number, number] = [255, 255, 255];
  const black: [number, number, number] = [0, 0, 0];
  return {
    '50': mix(white, base, 0.05),
    '100': mix(white, base, 0.15),
    '200': mix(white, base, 0.3),
    '300': mix(white, base, 0.5),
    '400': mix(white, base, 0.7),
    '500': mix(base, black, 0.0),
    '600': mix(base, black, 0.15),
    '700': mix(base, black, 0.3),
    '800': mix(base, black, 0.5),
    '900': mix(base, black, 0.65),
  };
}

export interface BrandTheme {
  brand_name: string;
  /** Logo URL for light mode */
  logo_url: string | null;
  /** Logo URL for dark mode – falls back to logo_url if absent */
  logo_dark_url: string | null;
  primary_color: string;
  secondary_color: string;
  /** Optional navigation/sidebar background color; empty string = use default */
  nav_bg_color: string;
  subdomain: string | null;
  is_whitelabel: boolean;
  referral_code: string | null;
  /** Reseller's custom privacy policy URL (whitelabel only, empty string if not set) */
  privacy_policy_url: string;
  /** Reseller's custom terms URL (whitelabel only, empty string if not set) */
  terms_url: string;
  /** Platform footer text (copyright notice), configurable via admin */
  footer_text: string;
  /** Meta Pixel ID from the admin; empty for white-label brands or when tracking is off */
  meta_pixel_id: string;
}

const DEFAULT_THEME: BrandTheme = {
  brand_name: 'novofon',
  logo_url: null,
  logo_dark_url: null,
  primary_color: '#45D0BD',
  secondary_color: '#8258C8',
  nav_bg_color: '',
  subdomain: null,
  is_whitelabel: false,
  referral_code: null,
  privacy_policy_url: '',
  terms_url: '',
  footer_text: '',
  meta_pixel_id: '',
};

interface BrandContextType {
  brand: BrandTheme;
  isLoading: boolean;
}

const BrandContext = createContext<BrandContextType>({
  brand: DEFAULT_THEME,
  isLoading: true,
});

// eslint-disable-next-line react-refresh/only-export-components
export const useBrand = () => useContext(BrandContext);

interface BrandProviderProps {
  children: ReactNode;
}

export const BrandProvider: React.FC<BrandProviderProps> = ({ children }) => {
  const [brand, setBrand] = useState<BrandTheme>(DEFAULT_THEME);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const loadTheme = async () => {
      try {
        const hostname = window.location.hostname;
        const apiBase = getApiBaseUrl();
        const res = await fetch(
          `${apiBase}/resellers/theme/?domain=${encodeURIComponent(hostname)}`
        );
        if (res.ok) {
          const data: Partial<BrandTheme> = await res.json();
          setBrand({ ...DEFAULT_THEME, ...data });
        }
      } catch {
        // Use default theme on error
      } finally {
        setIsLoading(false);
      }
    };
    loadTheme();
  }, []);

  // Apply CSS custom properties
  useEffect(() => {
    const root = document.documentElement;
    document.title = brand.brand_name;

    const shades = ['50', '100', '200', '300', '400', '500', '600', '700', '800', '900'];

    if (brand.is_whitelabel) {
      // Override Tailwind palette CSS variables with brand colors
      const primary = buildPalette(brand.primary_color);
      const secondary = buildPalette(brand.secondary_color);
      Object.entries(primary).forEach(([k, v]) => root.style.setProperty(`--tw-primary-${k}`, v));
      Object.entries(secondary).forEach(([k, v]) =>
        root.style.setProperty(`--tw-secondary-${k}`, v)
      );

      // Accent (no longer a separate field – falls back to primary via CSS var)
      shades.forEach(k => root.style.removeProperty(`--tw-accent-${k}`));

      // Navigation colors – injected as a real stylesheet so .dark selectors work
      if (brand.nav_bg_color) {
        injectBrandNavStyles(brand.nav_bg_color);
      } else {
        removeBrandNavStyles();
      }
    } else {
      // Restore defaults (remove inline overrides → falls back to :root in tailwind.scss)
      shades.forEach(k => {
        root.style.removeProperty(`--tw-primary-${k}`);
        root.style.removeProperty(`--tw-secondary-${k}`);
        root.style.removeProperty(`--tw-accent-${k}`);
      });
      removeBrandNavStyles();
    }
  }, [brand]);

  return <BrandContext.Provider value={{ brand, isLoading }}>{children}</BrandContext.Provider>;
};
